<template>
  <div
    class="field-comparison-diff"
    :class="classes"
    @click="onToggleExpand"
  >
    <div class="left-container d-flex flex-column">
      <div class="left flex-grow-1">
        {{ left }}
        <div v-if="expandable" class="fade-out-overlay"></div>
      </div>
      <div
        v-if="expandable"
        class="expand-text caption"
        :class="classes"
      >
        {{ expandText }}
      </div>
    </div>
    <div class="right-container d-flex flex-column">
      <div class="right flex-grow-1">
        {{ right }}
        <div v-if="expandable" class="fade-out-overlay"></div>
      </div>
      <div
        v-if="expandable"
        class="expand-text caption"
        :class="classes"
      >
        {{ expandText }}
        <div v-if="hasScore" class="similarity-chip-container">
          <similarity-chip :score="score" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SimilarityChip from './SimilarityChip';
import { PublicationFieldDiffType } from '@/enums';

export default {
  name: 'FieldComparison',
  components: { SimilarityChip },
  props: {
    left: {
      type: [String, Number],
      default: null,
    },
    right: {
      type: [String, Number],
      default: null,
    },
    diffType: {
      type: String,
      required: true,
      validator: (val) =>
        Object.values(PublicationFieldDiffType).includes(val),
    },
    score: {
      type: Number,
      default: null,
    },
    expandable: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({ expanded: false }),
  computed: {
    hasScore() {
      return this.score !== null && this.left !== this.right;
    },
    classes() {
      return [
        this.diffType.toLowerCase(),
        { expanded: this.expanded },
        { expandable: this.expandable },
      ];
    },
    expandText() {
      return this.expanded ? '- Show Less' : '+ Show All';
    },
  },
  methods: {
    onToggleExpand() {
      if (this.expandable) {
        this.expanded = !this.expanded;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~vuetify/src/styles/styles.sass';

$collapsed-height: 80px;
$starting-opacity: 0;
$middle-opacity: 0.7;

.fade-out-overlay {
  // Fade the text out when expandable (white by default)
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, $starting-opacity) 0%,
    rgba(255, 255, 255, $middle-opacity) 50%,
    #ffffff 100%
  );
}

.left-container {
  border-right: 1px solid map-get($grey, 'lighten-2');
}

.left-container,
.right-container {
  background-color: white;
}

.add .left-container,
.missing .right-container,
.mismatch .right-container {
  // Apply light red colour to the background.
  background-color: #fff1f1;
}

.add .left,
.missing .right,
.mismatch .right {
  // Fade out text using light red colour.
  .fade-out-overlay {
    background: linear-gradient(
      to bottom,
      rgba(255, 241, 241, $starting-opacity) 0%,
      rgba(255, 241, 241, $middle-opacity) 50%,
      rgba(255, 241, 241, 1) 100%
    );
  }
}

.match .right-container {
  // Apply light green colour to the background.
  background-color: #f1fff4;
}

.match .right {
  // Fade out text using light green colour.
  .fade-out-overlay {
    background: linear-gradient(
      to bottom,
      rgba(241, 255, 244, $starting-opacity) 0%,
      rgba(241, 255, 244, $middle-opacity) 50%,
      rgba(241, 255, 244, 1) 100%
    );
  }
}

.field-comparison-diff {
  font-size: 14px;
  // Put the values side by side
  display: grid;
  grid-template-columns: 1fr 1fr;

  // If the field is empty, make sure it maintains height.
  min-height: 45px;

  .left,
  .right {
    padding: 12px 16px 12px 16px;
  }

  &.expandable {
    cursor: pointer;
  }

  &.expandable .left,
  &.expandable .right {
    // Assist positioning of the fade out overlay.
    position: relative;

    // Restrict the height of the text if expandable.
    overflow: hidden;
    max-height: $collapsed-height;
    transition: max-height 0.2s ease-out;
  }

  .fade-out-overlay {
    // Position overlay above parent and cover completely.
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
  }

  &.expanded {
    // Remove the linear gradient background when expanded.
    .fade-out-overlay {
      background: none !important;
    }

    // Expand the text to its full height.
    .left,
    .right {
      max-height: 100%;
    }
  }
}

.expand-text {
  // Assist the positioning of the similarity chip.
  position: relative;

  // Make the height of the height of the view more than the similarity chip.
  height: 50px;

  // Center the view more text in the container.
  display: flex;
  justify-content: center;
  align-items: center;

  // Prevent user from selecting the text.
  user-select: none;

  color: map-get($grey, 'darken-1');
}

.left-container:hover,
.right-container:hover {
  // Make "Show All" text black on hover of the content.
  .expand-text {
    color: black !important;
  }
}

.similarity-chip-container {
  // Position the chip container on top of the parent.
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;

  // Center and align the chip to the right.
  display: grid;
  justify-content: end;
  align-items: center;

  // Offset the chip to the right.
  padding-right: 12px;

  // Allow pointer events to pass through.
  pointer-events: none;
}
</style>
